// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getAccountsDataView } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import loadAccounts from "@insite/client-framework/Store/Data/Accounts/Handlers/LoadAccounts";
import updateSearchFields from "@insite/client-framework/Store/Pages/UserList/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import { WishListModel } from "@insite/client-framework/Types/ApiModels";
import { userListUsersTableStyles } from "@insite/content-library/Widgets/UserList/UserListUsersTable";
import { ButtonProps } from "@insite/mobius/Button";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Button, Checkbox, GridContainer, GridItem, LoadingOverlay, Modal, Typography } from "@insite/mobius/index";
import { ModalProps } from "@insite/mobius/Modal";
import Pagination from "@store/MobiusOverrides/Pagination/Pagination";
import useBuyingList from "@store/Framework/Common/Hooks/useBuyingList/useBuyingList";
import { useDebounce } from "@store/Framework/Common/Hooks/useDebounce";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import SearchBox from "@store/Library/Components/Bunzl/Common/SearchBox";
import ManageUsersModalTable from "@store/Library/Components/BuyingList/ManageUsersModal/ManageUsersModalTable";
import FadeIn from "@store/Library/Components/Reporting/Common/FadeIn";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import { getMediaQuery } from "@toolkit/Framework/Common/Utilities/mediaQueries";
import { useCallback, useEffect, useRef } from "react";
import React from "react";
import styled, { css } from "styled-components";

const mobileQuery = getMediaQuery({ option: "only", first: true, min: 0, max: 600, last: false });
interface ManageProductsModalProps {
    buyingList: WishListModel | null;
    selectedUsers: string[];
    onClose: () => void;
    isOpen: boolean;
}
export const StyledModal = styled(Modal)`
    div[role="dialog"] {
        min-width: 90vw;
        height: 80vh;
    }
`;

interface ManageProductsExtendedStyles {
    modal: ModalProps;
    flexGridContainer?: GridContainerProps;
    topSectionGridContainer?: GridContainerProps;
    paginationGridItem: GridItemProps;
    assignButtonGridItem?: GridItemProps;
    assignSelectedButton: ButtonProps;
    infosBox: InfosBoxProps;
    linkDataTableCell: DataTableCellProps;

    paginationGridItemBottom: GridItemProps;
}

export const manageProductsExtendedStyles: ManageProductsExtendedStyles = {
    modal: {
        cssOverrides: {
            modalContent: css`
                overflow: visible;
                min-width: 70vw;
                min-height: 50vh;
            `,
            modalBody: css`
                overflow: auto;
                min-width: 70vw;
                min-height: 50vh;
            `,
        },

        headlineTypographyProps: {
            ellipsis: true,
            variant: "h3",
            css: css`
                text-align: center;
                max-width: 10rem;
            `,
        },
    },

    topSectionGridContainer: {
        css: css`
            padding-inline: ${pxToRem(15)};
        `,
        offsetCss: css`
            min-height: 5rem;
        `,
    },
    paginationGridItem: {
        css: css`
            padding: 0;

            justify-content: center;
            align-items: center;
            ${mobileQuery} {
                padding-bottom: 1.5rem;
            }
        `,
        width: [12, 12, 4, 4, 4],
    },
    assignButtonGridItem: {
        css: css`
            display: flex;
            padding: 1rem;

            justify-content: flex-end;
            column-gap: 1rem;
            align-items: center;
        `,
        width: 4,
    },
    assignSelectedButton: {
        css: css`
            border-radius: 0.5rem;
            font-size: 1rem;
        `,
    },
    infosBox: {
        extendedStyles: {
            wrapper: {
                css: css`
                    // !important is needed because some selector is overriding on the nth child
                    margin-bottom: 1rem !important;
                `,
            },
        },
        type: "warning" as const,
    },
    linkDataTableCell: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 14rem;
                display: block;
            `,
        },
    },

    paginationGridItemBottom: {
        css: css`
            justify-content: center;
            align-items: center;
        `,
        width: 12,
    },
};
const ManageUsersModal: React.FC<ManageProductsModalProps> = ({ buyingList, onClose, isOpen }) => {
    const [selectedUsers, setSelectedUsers] = React.useState<string[]>([]);
    const dispatch = useAppDispatch();
    const hasLoaded = useRef(false);
    const parameter = useAppSelector(state => state.pages.userList.getAccountsParameter);
    const userAccountsDataView = useAppSelector(state => getAccountsDataView(state, parameter));
    const { assignedBuyingList, updateAssignedUsers, getAssignedBuyingList } = useBuyingList();
    const buyingListId = buyingList?.properties?.buyingListId || "";
    useEffect(() => {
        if (!isOpen) {
            return;
        }
        if (buyingListId && !assignedBuyingList) {
            getAssignedBuyingList(buyingListId);
        }
    }, [buyingListId, isOpen]);

    const handleSearch = useCallback(
        (term: string) => {
            if (parameter.searchText !== term) {
                dispatch(updateSearchFields({ searchText: term }));
            }
        },
        [parameter.searchText, dispatch],
    );
    const debouncedSearch = useDebounce(handleSearch, 400);
    const assigned = assignedBuyingList?.users.map(user => user.id) || [];
    const handleChangePage = (page: number) => {
        if (page !== parameter.page) {
            setSelectedUsers([]);
            dispatch(updateSearchFields({ page }));
        }
    };
    const handleChangeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = Number(event.target.value);
        if (newPageSize !== parameter.pageSize) {
            dispatch(
                updateSearchFields({
                    page: 1,
                    pageSize: newPageSize,
                }),
            );
        }
    };
    // needed to clean up parameter to not mess with the user list
    useEffect(() => {
        return () => {
            dispatch(updateSearchFields({ ...parameter, excludeRoles: undefined }));
        };
    }, []);
    useEffect(() => {
        if (!parameter?.excludeRoles?.includes("Administrator")) {
            dispatch(updateSearchFields({ ...parameter, excludeRoles: ["Administrator"] }));
            return;
        }
        if (!isOpen) {
            return;
        }
        // prevent initial load if we already have data
        if (hasLoaded.current && !userAccountsDataView?.value) {
            dispatch(loadAccounts(parameter));
        } else {
            hasLoaded.current = true;
        }
    }, [parameter, isOpen, userAccountsDataView?.value, dispatch]);

    const loading = userAccountsDataView.isLoading;
    // eslint-disable-next-line react/jsx-key
    const userListHeaders = [
        // eslint-disable-next-line react/jsx-key
        <Checkbox
            disabled={loading}
            onChange={() => {
                if (selectedUsers.length === userAccountsDataView.value?.length) {
                    setSelectedUsers([]);
                } else {
                    setSelectedUsers(userAccountsDataView?.value?.map(user => user.id) || []);
                }
            }}
            checked={
                !!userAccountsDataView.value?.length && selectedUsers.length === userAccountsDataView?.value?.length
            }
        />,
        translate("Name"),
        translate("Email"),
        translate("Action"),
    ];
    const handleClose = () => {
        onClose();
    };
    const handleAssign = (id: string) => {
        if (assigned.includes(id)) {
            updateAssignedUsers(
                buyingListId,
                assigned.filter(item => item !== id),
            );
        } else {
            updateAssignedUsers(buyingListId, [...assigned, id]);
        }
    };

    const styles = userListUsersTableStyles;
    const handleAssignSelected = () => {
        const newAssigned = [...new Set([...assigned, ...selectedUsers])];
        updateAssignedUsers(buyingListId, newAssigned);
        setSelectedUsers([]);
    };
    const handleUnassignSelected = () => {
        const newAssigned = assigned.filter(item => !selectedUsers.includes(item));
        updateAssignedUsers(buyingListId, newAssigned);
        setSelectedUsers([]);
    };
    return (
        <>
            <StyledModal
                {...manageProductsExtendedStyles.modal}
                title={buyingList?.name}
                headline={translate(`Add or Remove Users from ${buyingList?.name}`)}
                isOpen={isOpen}
                handleClose={handleClose}
                data-test-selector="buyingListActions_manageUsersModal"
            >
                <GridContainer {...manageProductsExtendedStyles.flexGridContainer}>
                    <GridContainer {...manageProductsExtendedStyles.topSectionGridContainer}>
                        <SearchBox
                            placeholder={translate("Filter Users by Name or Email")}
                            extendedStyles={{
                                gridItem: {
                                    width: [12, 12, 4, 4, 4],
                                },
                            }}
                            title=""
                            onSearch={debouncedSearch}
                        />

                        <GridItem {...manageProductsExtendedStyles.paginationGridItem}>
                            {"pagination" in userAccountsDataView && (
                                <Pagination
                                    // this prop does exist its just not typed
                                    // @ts-ignore
                                    hidePerPageSelect={true}
                                    resultsCount={userAccountsDataView?.pagination?.totalItemCount || 0}
                                    currentPage={userAccountsDataView?.pagination?.page || 1}
                                    resultsPerPage={userAccountsDataView?.pagination?.pageSize || 1}
                                    resultsPerPageOptions={userAccountsDataView?.pagination?.pageSizeOptions || []}
                                    onChangePage={handleChangePage}
                                    onChangeResultsPerPage={handleChangeResultsPerPage}
                                    data-test-selector="userListUsersTable_pagination"
                                />
                            )}
                        </GridItem>

                        {selectedUsers.length > 0 && (
                            <GridItem {...manageProductsExtendedStyles.assignButtonGridItem}>
                                <FadeIn animate appear duration={200}>
                                    <Button
                                        onClick={handleAssignSelected}
                                        {...manageProductsExtendedStyles.assignSelectedButton}
                                    >
                                        {translate("Assign Selected")}
                                    </Button>
                                </FadeIn>
                                <FadeIn animate appear duration={200}>
                                    <Button
                                        onClick={handleUnassignSelected}
                                        variant="tertiary"
                                        {...manageProductsExtendedStyles.assignSelectedButton}
                                    >
                                        {translate("Unassign Selected")}
                                    </Button>
                                </FadeIn>
                            </GridItem>
                        )}
                    </GridContainer>

                    {selectedUsers.length > 0 &&
                        userAccountsDataView?.value &&
                        (userAccountsDataView.pagination?.numberOfPages || 1) > 1 && (
                            <FadeIn animate appear duration={200}>
                                <InfosBox {...manageProductsExtendedStyles.infosBox}>
                                    {siteMessage("UserSetup_AssignShipTo_PagerWarning")}
                                </InfosBox>
                            </FadeIn>
                        )}
                    <GridItem
                        css={css`
                            ${mobileQuery} {
                                padding: 0;
                            }
                        `}
                        width={12}
                    >
                        <LoadingOverlay loading={loading}>
                            <ManageUsersModalTable
                                selectedUsers={selectedUsers}
                                setSelectedUsers={setSelectedUsers}
                                userAccountsDataView={userAccountsDataView}
                                assignedUsers={assigned}
                                loading={loading}
                                userListHeaders={userListHeaders}
                                handleAssign={handleAssign}
                            ></ManageUsersModalTable>
                        </LoadingOverlay>
                    </GridItem>
                    {selectedUsers.length > 0 &&
                        userAccountsDataView?.value &&
                        (userAccountsDataView.pagination?.numberOfPages || 1) > 1 && (
                            <FadeIn animate={true} appear={true} duration={200}>
                                <InfosBox type="warning">{siteMessage("UserSetup_AssignShipTo_PagerWarning")}</InfosBox>
                            </FadeIn>
                        )}
                    {userAccountsDataView?.value && !userAccountsDataView?.value?.length && !loading && (
                        <StyledWrapper {...styles.noResultsContainer}>
                            <Typography
                                as="p"
                                {...styles.noResultsText}
                                data-test-selector="userListUsersTable_noUsersFound"
                            >
                                {translate("No users found")}
                            </Typography>
                        </StyledWrapper>
                    )}

                    {"pagination" in userAccountsDataView && (
                        <GridItem {...manageProductsExtendedStyles.paginationGridItemBottom}>
                            <Pagination
                                // this prop does exist its just not typed
                                // @ts-ignore
                                hidePerPageSelect={true}
                                resultsCount={userAccountsDataView?.pagination?.totalItemCount || 0}
                                currentPage={userAccountsDataView?.pagination?.page || 1}
                                resultsPerPage={userAccountsDataView?.pagination?.pageSize || 1}
                                resultsPerPageOptions={userAccountsDataView?.pagination?.pageSizeOptions || []}
                                onChangePage={handleChangePage}
                                onChangeResultsPerPage={handleChangeResultsPerPage}
                                data-test-selector="userListUsersTable_pagination"
                            />
                        </GridItem>
                    )}
                </GridContainer>
            </StyledModal>
        </>
    );
};

export default ManageUsersModal;
